import { IGroupInfo, IProductListItem } from '@/api/types/product';
import { INavListItem } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { CUSTOM_VALUE, DEFAULT_SORT_VALUE } from '@/utils/constants';
import { EProductImageType } from '@/utils/enums/goods';
import { isEmpty } from '@/utils/tools';
import { env } from './env';

interface HandleGoodsListSeoParams {
  id: string;
  list: IProductListItem[];
  group: IGroupInfo;
  navData?: INavListItem[];
  routeName?: string;
}

interface SeoInfo {
  groupInfo: IGroupInfo;
  linkData: Array<{
    rel: string;
    href: string;
  }>;
  scriptData: Array<{
    innerHTML: string;
    type: string;
  }>;
}

/**
 * @description: 用于处理商品列表页的seo数据信息
 * @return {*}
 */
export function handleGoodsListSeo(params: HandleGoodsListSeoParams): SeoInfo {
  const { id, list, group, navData, routeName } = params;
  let pagePath = 'goods-list';
  if (routeName === 'CrowdProductList') {
    pagePath = 'product-list';
  }

  const linkArr = [
    {
      rel: 'canonical',
      href: `${env.NEXT_PUBLIC_FULL_URL}/${pagePath}/${id}`,
    },
  ];

  const scriptArr = [
    {
      innerHTML: `
            {
                "@context": "https://schema.org/",
                "@type": "Organization",
                "url": "${env.NEXT_PUBLIC_FULL_URL}",
                "logo": "https://s3.zeelool.com/admin/shop/home/2a1e01e7f71698f12a598739898362a1.png"
            }
            `,
      type: 'application/ld+json',
    },
    {
      innerHTML: `
            {
                "@context": "https://schema.org/",
                "@type": "WebSite",
                "name": "ZEELOOL",
                "alternateName": "Zeelool Glasses",
                "url": "${env.NEXT_PUBLIC_FULL_URL}",
                "potentialAction": {
                    "@type": "SearchAction",
                    "target":"${env.NEXT_PUBLIC_FULL_URL}/search-result?keywords={search_term_string}",
                    "query-input": "required name=search_term_string"
                }
            }
            `,
      type: 'application/ld+json',
    },
  ];

  try {
    const itemListArr: Array<{
      '@type': string;
      position: number;
      item: {
        '@type': string;
        image: string;
        url: string;
        name: string;
        offers: {
          '@type': string;
          price: string;
          priceCurrency: string;
          url: string;
        };
      };
    }> = [];

    if (list.length) {
      list.forEach((el, index) => {
        if (el.isAdsBanner || el.data_type === 'adv') {
          return;
        }
        itemListArr.push({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Product',
            image: `${(el.image && el.image.product && el.image.product[0]) || ''}`,
            url: `${env.NEXT_PUBLIC_FULL_URL}/goods-detail/${el.shop_sku}`,
            name: `${el.short_name}`,
            offers: {
              '@type': 'Offer',
              price: `${el.price_info[0].sell_price}`,
              priceCurrency: `${el.price_info[0].currency}`,
              url: `${env.NEXT_PUBLIC_FULL_URL}/goods-detail/${el.shop_sku}`,
            },
          },
        });
      });
    }

    scriptArr.push({
      innerHTML: `
                {
                    "@context": "http://schema.org",
                    "@type": "ItemList",
                    "itemListElement": ${JSON.stringify(itemListArr)}
                }
            `,
      type: 'application/ld+json',
    });

    if (navData && navData.length) {
      const simpleNavData = handleNavData(navData).totalNavList;
      if (simpleNavData && simpleNavData.length) {
        const navObj = simpleNavData.find((e) => Number(e.id) === Number(id));
        if (!isEmpty(navObj)) {
          const breadListArr = [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@id': `${env.NEXT_PUBLIC_FULL_URL}/`,
                name: 'Zeelool',
              },
            },
          ];

          // 使用类型断言确保navObj具有所需属性
          const navItem = navObj as any;
          if (navItem?.level === 1) {
            breadListArr.push({
              '@type': 'ListItem',
              position: 2,
              item: {
                '@id': `${navItem?.url || ''}`,
                name: `${navItem?.name || ''}`,
              },
            });

            scriptArr.push({
              innerHTML: `
                                {
                                    "@context": "http://schema.org",
                                    "@type": "BreadcrumbList",
                                    "itemListElement": ${JSON.stringify(breadListArr)}
                                }
                            `,
              type: 'application/ld+json',
            });
          }
        }
      }
    }
  } catch (e) {
    logger.log(e);
  }

  // 获取到seoInfo
  const seoInfo: SeoInfo = {
    groupInfo: group,
    linkData: linkArr,
    scriptData: scriptArr,
  };

  return seoInfo;
}

/**
 * 处理导航数据
 */
export function handleNavData(arr: INavListItem[]): { totalNavList: INavListItem[] } {
  const totalNavList: INavListItem[] = []; // 魔方配置导航列表

  try {
    arr.forEach((elm) => {
      if (elm.children) {
        elm.children.forEach((e) => {
          // 使用类型断言处理content属性
          const content = e.content as any;
          if (
            content.type === 1 &&
            (content.templateIndex === 1 ||
              content.templateIndex === 2 ||
              content.templateIndex === 3)
          ) {
            content.list?.forEach((d: any) => {
              const { jump_type, Jump_target, Jump_title, product_group: { id } = {} } = d;
              if (jump_type === 1) {
                totalNavList.push({
                  id: Number(id || Jump_target) || 0,
                  level: 1,
                  url: `${env.NEXT_PUBLIC_FULL_URL}/goods-list/${id || Jump_target}`,
                  name: Jump_title || String(id || Jump_target),
                } as any);
              }
            });
          } else if (content.type === 2) {
            if (content.submenu_list && content.submenu_list.length) {
              content.submenu_list.forEach((es: any) => {
                const { jump_type, Jump_target, Jump_title, product_group: { id } = {} } = es;
                if (jump_type === 1) {
                  totalNavList.push({
                    id: Number(id || Jump_target) || 0,
                    level: 1,
                    url: `${env.NEXT_PUBLIC_FULL_URL}/goods-list/${id || Jump_target}`,
                    name: Jump_title || String(id || Jump_target),
                  } as any);
                }
              });
            } else {
              const { jump_type, Jump_target, title, product_group: { id } = {} } = content;
              if (jump_type === 1) {
                totalNavList.push({
                  id: Number(id || Jump_target) || 0,
                  level: 1,
                  url: `${env.NEXT_PUBLIC_FULL_URL}/goods-list/${id || Jump_target}`,
                  name: title || String(id || Jump_target),
                } as any);
              }
            }
          }
        });
      }
    });
  } catch (e) {
    logger.log(e);
  }

  return {
    totalNavList,
  };
}
/**
 * 尝试与下一个元素交换位置
 * @param {Array} list - 商品列表
 * @param {number} index - 当前处理的索引
 * @returns {boolean} - 是否成功交换
 * 条件：当前项的下一项存在，且是单坑位
 * 操作：将当前双坑位项与下一个单坑位项交换位置
 */
const trySwapWithNext = (list: IProductListItem[], index: number) => {
  if (list[index + 1] && isSinglePit(list[index + 1])) {
    const newList = [...list];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    // 将修改应用回原数组
    list.splice(0, list.length, ...newList);
    return true;
  }
  return false;
};

/**
 * 尝试与上一个元素交换位置
 * @param {Array} list - 商品列表
 * @param {number} index - 当前处理的索引
 * @returns {boolean} - 是否成功交换
 * 条件：当前项的下一项不存在，上一项存在且是单坑位
 * 操作：将当前双坑位项与上一个单坑位项交换位置
 */
const trySwapWithPrev = (list: IProductListItem[], index: number) => {
  if (!list[index + 1] && list[index - 1] && isSinglePit(list[index - 1])) {
    const newList = [...list];
    [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
    // 将修改应用回原数组
    list.splice(0, list.length, ...newList);
    return true;
  }
  return false;
};
/**
 * 尝试与上一个元素交换位置
 * @param {Array} list - 商品列表
 * @param {number} index - 当前处理的索引
 * @returns {boolean} - 是否成功交换
 * 条件：当前项的下一项存在且是双坑位，上一项存在且是单坑位
 * 操作：将当前双坑位项与上一个单坑位项交换位置
 */
const trySwapWithPrevColumn2 = (list: IProductListItem[], index: number) => {
  if (
    list[index + 1] &&
    isDoublePit(list[index + 1]) &&
    list[index - 1] &&
    isSinglePit(list[index - 1])
  ) {
    const newList = [...list];
    [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
    // 将修改应用回原数组
    list.splice(0, list.length, ...newList);
    return true;
  }
  return false;
};
/**
 * 尝试将当前双坑位转换为单坑位
 * @param {Array} list - 商品列表
 * @param {number} index - 当前处理的索引
 * @returns {boolean} - 是否成功转换
 * 条件：当前项的下一项不存在，上一项存在且是双坑位
 * 操作：将当前双坑位项转换为单坑位
 */
const tryConvertToSingle = (list: IProductListItem[], index: number) => {
  if (!list[index + 1] && list[index - 1] && isDoublePit(list[index - 1])) {
    const newItem = { ...list[index], type: 1 };
    // 将修改应用回原数组
    list.splice(index, 1, newItem);
    return true;
  }
  return false;
};

const getAllPitsLength = (list: IProductListItem[]) => {
  let pitCount = 0;
  list.forEach((item) => {
    if (item.data_type === 'adv' && item.type === 2) {
      pitCount += 2;
    } else {
      pitCount += 1;
    }
  });
  return pitCount;
};

const isSinglePit = (item: IProductListItem) => {
  return item.data_type !== 'adv' || (item.data_type === 'adv' && item.type === 1);
};

const isDoublePit = (item: IProductListItem) => {
  return item.data_type === 'adv' && item.type === 2;
};

// 深拷贝函数
const deepClone = (obj: IProductListItem[]): IProductListItem[] => {
  return JSON.parse(JSON.stringify(obj)) as IProductListItem[];
};

// 处理商品列表广告位和商品的排列
export const handleListLayout = (_list: IProductListItem[], columnCount: number) => {
  let index = 0;
  const list = deepClone(_list);

  while (index < list.length) {
    const item = list[index];
    if (!isDoublePit(item)) {
      index++;
      continue;
    }

    // 获取当前位置之前的坑位总数
    const pitCount = getAllPitsLength(list.slice(0, index));
    const needAdjust =
      columnCount === 2
        ? pitCount % 2 !== 0 // 两列布局：不能被2整除需要调整
        : pitCount % 3 === 2; // 三列布局：余2需要调整

    if (!needAdjust) {
      index++;
      continue;
    }

    // 尝试调整布局
    if (
      trySwapWithNext(list, index) ||
      trySwapWithPrev(list, index) ||
      (columnCount === 3 && tryConvertToSingle(list, index)) ||
      (columnCount === 2 && trySwapWithPrevColumn2(list, index))
    ) {
      index = 0; // 重新开始遍历
    } else {
      index++;
    }
  }
  return list;
};

// 处理HTML文本，保留所有标签，将\n换行符转换为<br>标签
// export const getHtmlText = (_text: string) => {
//   if (!_text) return '';

//   try {
//     // 预处理：将所有 \n 换行符转换为 <br> 标签
//     const processedText = _text.replace(/\n/g, '<br>');

//     // 使用DOMParser解析HTML
//     const parser = new DOMParser();
//     const doc = parser.parseFromString(processedText, 'text/html');

//     // 递归处理节点，保留所有标签
//     const processNode = (node: Node): string => {
//       if (node.nodeType === Node.TEXT_NODE) {
//         return node.textContent || '';
//       }

//       if (node.nodeType === Node.ELEMENT_NODE) {
//         const element = node as Element;

//         // 获取所有属性
//         const attributes = Array.from(element.attributes)
//           .map((attr) => ` ${attr.name}="${attr.value}"`)
//           .join('');

//         // 处理子节点
//         const innerContent = Array.from(element.childNodes)
//           .map((child) => processNode(child))
//           .join('');

//         // 特殊处理自闭合标签
//         if (['br', 'hr', 'img', 'input', 'meta', 'link'].includes(element.tagName.toLowerCase())) {
//           return `<${element.tagName.toLowerCase()}${attributes}>`;
//         }

//         // 为a标签添加默认样式类
//         if (element.tagName.toLowerCase() === 'a') {
//           const hasClass = element.getAttribute('class');
//           const classAttr = hasClass
//             ? ` class="${hasClass} underline text-primary-purple"`
//             : ` class="underline text-primary-purple"`;

//           // 移除原有的class属性，添加新的
//           const otherAttrs = Array.from(element.attributes)
//             .filter((attr) => attr.name !== 'class')
//             .map((attr) => ` ${attr.name}="${attr.value}"`)
//             .join('');

//           return `<${element.tagName.toLowerCase()}${otherAttrs}${classAttr}>${innerContent}</${element.tagName.toLowerCase()}>`;
//         }

//         // 其他标签正常处理
//         return `<${element.tagName.toLowerCase()}${attributes}>${innerContent}</${element.tagName.toLowerCase()}>`;
//       }

//       return '';
//     };

//     // 处理body中的所有内容
//     const result = Array.from(doc.body.childNodes)
//       .map((child) => processNode(child))
//       .join('');

//     return result;
//   } catch (error) {
//     logger.error('Error processing HTML text:', error);
//     // 如果解析失败，至少处理换行符
//     return _text.replace(/\n/g, '<br>');
//   }
// };

// 处理收起状态的文本：只保留a标签，移除其他所有HTML元素
// export const getCollapsedText = (_text: string) => {
//   if (!_text) return '';

//   try {
//     // 使用DOMParser解析HTML
//     const parser = new DOMParser();
//     const doc = parser.parseFromString(_text, 'text/html');

//     // 递归处理节点，只保留文本和a标签
//     const processNode = (node: Node): string => {
//       if (node.nodeType === Node.TEXT_NODE) {
//         return node.textContent || '';
//       }

//       if (node.nodeType === Node.ELEMENT_NODE) {
//         const element = node as Element;
//         const tagName = element.tagName.toLowerCase();

//         // 如果是a标签，保留它
//         if (tagName === 'a') {
//           const href = element.getAttribute('href');
//           const target = element.getAttribute('target');
//           const rel = element.getAttribute('rel');

//           let attributes = '';
//           if (href) attributes += ` href="${href}"`;
//           if (target) attributes += ` target="${target}"`;
//           if (rel) attributes += ` rel="${rel}"`;

//           const innerContent = Array.from(element.childNodes)
//             .map((child) => processNode(child))
//             .join('');

//           return `<a${attributes} class="underline text-primary-purple">${innerContent}</a>`;
//         }

//         // br标签直接转换为空格
//         if (tagName === 'br') {
//           return ' ';
//         }

//         // 处理子节点
//         const processedChildren = Array.from(element.childNodes)
//           .map((child) => processNode(child))
//           .filter((text) => text !== ''); // 过滤空字符串

//         const innerText = processedChildren.join('');

//         // 如果没有内容，返回空字符串
//         if (!innerText.trim()) {
//           return '';
//         }

//         // 块级元素：在前后添加空格，确保与其他内容分离
//         if (
//           [
//             'div',
//             'p',
//             'h1',
//             'h2',
//             'h3',
//             'h4',
//             'h5',
//             'h6',
//             'ul',
//             'ol',
//             'li',
//             'section',
//             'article',
//             'header',
//             'footer',
//           ].includes(tagName)
//         ) {
//           return ` ${innerText.trim()} `;
//         }

//         // 内联元素：通常不需要额外空格，但某些情况下需要
//         if (['span', 'strong', 'em', 'b', 'i', 'code', 'small'].includes(tagName)) {
//           return innerText;
//         }

//         // 其他元素：添加空格以防止文本连接
//         return ` ${innerText.trim()} `;
//       }

//       return '';
//     };

//     // 处理body中的所有内容
//     const processedNodes = Array.from(doc.body.childNodes)
//       .map((child) => processNode(child))
//       .filter((text) => text !== ''); // 过滤空字符串

//     let result = processedNodes.join('');

//     // 智能清理空格：保留必要的空格，移除多余的连续空格
//     result = result
//       .replace(/\s+/g, ' ') // 将多个连续空格合并为一个
//       .replace(/\s+([.,!?;:])/g, '$1') // 移除标点符号前的空格
//       .replace(/([.,!?;:])\s*([.,!?;:])/g, '$1 $2') // 确保标点符号之间有适当空格
//       .trim(); // 移除首尾空格

//     return result;
//   } catch (error) {
//     logger.error('Error processing collapsed text:', error);
//     // 如果解析失败，返回纯文本
//     return _text
//       .replace(/<[^>]*>/g, ' ')
//       .replace(/\s+/g, ' ')
//       .trim();
//   }
// };

// 处理筛选参数转换的通用函数
export const transformFilterParams = (filterParams: any) => {
  const transformedFilter: any = {};
  let sellPrice: any[] = [];
  const temple = filterParams?.temple || ['0', '150'];
  const frame_width = filterParams?.frame_width || ['0', '150'];

  try {
    if (!filterParams || typeof filterParams !== 'object') {
      return transformedFilter;
    }

    // 移除init字段
    const cleanedParams = { ...filterParams };
    if (cleanedParams.init) delete cleanedParams.init;

    Object.entries(cleanedParams).forEach(([key, value]) => {
      if (!value) return;

      // 价格字段特殊处理
      if (key === 'sell_price') {
        try {
          if (value === 'on_sale') {
            sellPrice = ['on_sale'];
          } else if (value !== 'on_sale' && value) {
            const parsed = JSON.parse(value as string);
            sellPrice = Array.isArray(parsed) ? parsed : [parsed];
          }
          transformedFilter[key] = sellPrice;
        } catch (error) {
          logger.log('处理价格筛选参数失败:', error);
          sellPrice = [];
          transformedFilter[key] = [];
        }
      } else if (key === 'product_size' && value === 'custom') {
        transformedFilter.custom = {
          frame_leg_length: temple,
          frame_total_length: frame_width,
        };
      }
      // 其他字段直接赋值
      else if (!['temple', 'frame_width'].includes(key)) {
        transformedFilter[key] = value;
      }
    });
  } catch (error) {
    logger.log('处理筛选参数失败:', error);
  }

  return transformedFilter;
};

interface ProductFilterAttribute {
  attribute_code?: string;
  attribute_values?: Array<{
    name?: string;
    value?: string;
  }>;
}

const FILTER_RULE_FIELDS = [
  ['gender_list', 'product_gender'],
  ['size_list', 'product_size'],
  ['price_list', 'sell_price'],
  ['shape_list', 'product_frame_shape'],
  ['color_list', 'product_frame_color'],
  ['material_list', 'product_frame_material'],
  ['prescription_type_list', 'prescription_type'],
] as const;

const FILTER_RULE_RANGE_FIELDS = [
  ['frame_width', 'frame_width'],
  ['temple', 'temple'],
] as const;

const DEFAULT_CUSTOM_SIZE_RANGE = ['0', '150'];
const FILTER_RULE_DISPLAY_FALLBACKS: Record<string, Record<string, string>> = {
  product_size: {
    [CUSTOM_VALUE]: 'Custom',
  },
};

function getFilterDisplayValue(
  productFilterNew: ProductFilterAttribute[],
  attributeCode: string,
  value: string,
): string {
  const attributeConfig = productFilterNew.find(
    (config) => config.attribute_code === attributeCode,
  );
  const attributeValue = attributeConfig?.attribute_values?.find((item) => item.value === value);
  return attributeValue?.name || FILTER_RULE_DISPLAY_FALLBACKS[attributeCode]?.[value] || value;
}

function resolveFilterRuleValue(
  productFilterNew: ProductFilterAttribute[],
  attributeCode: string,
  rawValue: unknown,
): string {
  if (
    rawValue === '' ||
    rawValue === null ||
    rawValue === undefined ||
    (Array.isArray(rawValue) && rawValue.length === 0)
  ) {
    return '';
  }

  if (Array.isArray(rawValue)) {
    const values = rawValue
      .map((value) => getFilterDisplayValue(productFilterNew, attributeCode, String(value)))
      .filter(Boolean);
    if (values.length === 0) return '';
    return values.join(',');
  }

  return getFilterDisplayValue(productFilterNew, attributeCode, String(rawValue));
}

function resolveRangeFilterRuleValue(rawValue: unknown): string | string[] {
  if (!Array.isArray(rawValue) || rawValue.length === 0) {
    return '';
  }

  return rawValue.map((value) => String(value));
}

export function buildFilterRule(
  filterParams: Record<string, unknown> | null | undefined,
  productFilterNew: ProductFilterAttribute[] | null | undefined,
): string[] {
  const filter = filterParams || {};
  const filterConfig = Array.isArray(productFilterNew) ? productFilterNew : [];
  const isCustomSizeSelected = filter.product_size === CUSTOM_VALUE;

  return [
    ...FILTER_RULE_FIELDS.map(([ruleKey, attributeCode]) =>
      JSON.stringify({
        [ruleKey]: resolveFilterRuleValue(filterConfig, attributeCode, filter[attributeCode]),
      }),
    ),
    ...FILTER_RULE_RANGE_FIELDS.map(([ruleKey, attributeCode]) => {
      const rangeValue =
        isCustomSizeSelected && filter[attributeCode] === undefined
          ? DEFAULT_CUSTOM_SIZE_RANGE
          : filter[attributeCode];

      return JSON.stringify({
        [ruleKey]: resolveRangeFilterRuleValue(rangeValue),
      });
    }),
  ];
}

// 获取商品条目的标签, 神策埋点使用
/**
 * @param {*} item 商品item
 * @scene 1 商品列表页 2商品详情 3购物车猜你喜欢 4 商详页猜你喜欢
 */
export function getProductDisplayLabel(item: IProductListItem, scene: number) {
  const { products_promotions_type = '', is_new_in = '' } = item || {};
  // 接口返回的类型不统一，统一转为数字进行比较
  const productsPromotionsTypeNum = Number(products_promotions_type);
  const isNewInNum = Number(is_new_in);

  let labelArr = [];
  if (productsPromotionsTypeNum === 3) {
    labelArr = ['Seckill@'];
  } else if (productsPromotionsTypeNum === 2) {
    labelArr = ['Flash Sale@'];
  } else if (productsPromotionsTypeNum !== 1 && isNewInNum === 1 && [1, 3].includes(scene)) {
    labelArr = ['NEW IN@'];
  }
  // 详情页使用关联品中当前商品的标签
  if (scene === 2) {
    const relations = item.correlation;
    if (relations && relations.length > 0) {
      const currentItem = relations.find((relation) => relation.id === item.id);
      if (currentItem) {
        if (currentItem && currentItem.qty <= 0) {
          labelArr.push('Sold Out@');
        } else if (currentItem && String(currentItem.is_new_in) === '1') {
          labelArr.push('NEW@');
        }
      }
    }
  }
  if (scene === 1) {
    labelArr = [];
    if (productsPromotionsTypeNum === 3) {
      labelArr = ['Seckill@'];
    } else if (productsPromotionsTypeNum === 2) {
      labelArr = ['Flash Sale@'];
    } else if (isNewInNum === 1 && [1, 3].includes(scene)) {
      labelArr = ['NEW IN@'];
    }
    if (showBestSellerTag(item)) {
      labelArr.push('Best Seller@');
    }
  }

  if (scene === 4) {
    if (showBestSellerTag(item)) {
      labelArr.push('Best Seller@');
    }

    if (isNewInNum === 1) {
      labelArr.push('NEW IN@');
    }
  }

  return labelArr;
}
export function showBestSellerTag(productData: IProductListItem) {
  try {
    const isNotSpecialPromotion = !['3', '2'].includes(
      String(productData.products_promotions_type),
    );
    const isHot = String(productData.is_hot) === '1';
    return Boolean(isNotSpecialPromotion && isHot);
  } catch (error) {
    logger.error('Error in showBestSellerTag:', error);
    return false;
  }
}

// 获取商品的收藏数量
export function getWishNumber(item: IProductListItem) {
  const { wish_num = '' } = item || {};
  if (wish_num === '' || isNaN(Number(wish_num))) {
    return '';
  }
  return Number(wish_num);
}

// 提取图片处理逻辑到独立函数
export const getProductImages = (productData: any, _imageRule?: any, _imageHoverRule?: any) => {
  const imageRule = Array.isArray(_imageRule)
    ? _imageRule
    : [EProductImageType.PRODUCT, EProductImageType.MATERIAL, EProductImageType.CELEBRITY];
  const imageHoverRule = Array.isArray(_imageHoverRule)
    ? _imageHoverRule
    : [EProductImageType.MATERIAL, EProductImageType.CELEBRITY, EProductImageType.PRODUCT];
  try {
    const {
      product = [],
      product_gray = [],
      celebrity = [],
      material = [],
    } = productData?.image || {};

    const getProductImage = () => {
      // 非镜框商品，只显示主图
      if (productData.is_glass !== 1 && product.length > 0) {
        return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
      }
      for (const type of imageRule) {
        if (type === EProductImageType.PRODUCT) {
          if (product_gray.length > 0) {
            return { src: product_gray[0], type: EProductImageType.PRODUCT, isGray: true };
          } else if (product.length > 0) {
            return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
          }
        } else if (type === EProductImageType.CELEBRITY && celebrity.length > 0) {
          return { src: celebrity[0], type: EProductImageType.CELEBRITY, isGray: false };
        } else if (type === EProductImageType.MATERIAL && material.length > 0) {
          return { src: material[0], type: EProductImageType.MATERIAL, isGray: false };
        }
      }
      return { src: '' };
    };

    const getHoverImage = (productImg: any) => {
      if (imageHoverRule.length === 0 || productData.is_glass !== 1) {
        return { src: '' };
      }

      for (const type of imageHoverRule) {
        if (type === EProductImageType.PRODUCT) {
          if (productImg.type === EProductImageType.PRODUCT) {
            if (product_gray.length > 1) {
              return { src: product_gray[1], type: EProductImageType.PRODUCT, isGray: true };
            } else if (product_gray.length > 0) {
              return { src: product_gray[0], type: EProductImageType.PRODUCT, isGray: true };
            } else if (product.length > 1) {
              return { src: product[1], type: EProductImageType.PRODUCT, isGray: false };
            } else if (product.length > 0) {
              return { src: product[0], type: EProductImageType.PRODUCT, isGray: false };
            }
          }
        } else if (type === EProductImageType.CELEBRITY && celebrity.length > 0) {
          return { src: celebrity[0], type: EProductImageType.CELEBRITY, isGray: false };
        } else if (type === EProductImageType.MATERIAL && material.length > 0) {
          return { src: material[0], type: EProductImageType.MATERIAL, isGray: false };
        }
      }
      return { src: '' };
    };

    const productImage = getProductImage();
    const hoverImage = getHoverImage(productImage);

    return { productImage, hoverImage };
  } catch (error) {
    logger.error('Error in getProductImages:', error);
    return { productImage: { src: '' }, hoverImage: { src: '' } };
  }
};

/**
 * 获取完整的 HTML 文本（用于展开状态）
 */
export function getHtmlText(text: string): string {
  return (text || '').replace(/\n/g, '<br>');
}

/**
 * 获取收起状态的文本（移除复杂的 HTML 标签，保留 a 标签）
 */
export function getCollapsedText(text: string): string {
  if (!text) return '';

  // 移除除了 a 标签以外的所有 HTML 标签，但保留文本内容
  return text
    .replace(/<(?!\/?(a\b|\/a>))[^>]*>/gi, '') // 移除除 a 标签外的所有标签
    .replace(/\s+/g, ' ') // 合并多个空格为一个
    .trim();
}

/** 与 SortFilterBar 一致：将当前排序值解析为排序下拉的展示文案（配置项 name） */
export function getSortByLabel(
  currentSort: string | undefined,
  productSortNew: Array<{ name?: string; value?: string }> | undefined,
): string {
  const list = Array.isArray(productSortNew) ? productSortNew : [];
  const normalizedValue =
    currentSort === undefined || currentSort === null || currentSort === ''
      ? DEFAULT_SORT_VALUE
      : String(currentSort);
  const match = list.find((item) => String(item.value || DEFAULT_SORT_VALUE) === normalizedValue);
  return match?.name ?? normalizedValue;
}
